import type { WebSocketResponse } from "@/types/types";
import { BaseHandler } from "./base.handler";
import { WebSocketService } from "../websocket.service";
import {
  OptInTournament,
  OptOutTournament,
  RestartTournament,
  RESULT,
  type OptInTournamentResponse,
  type OptOutTournamentResponse,
} from "@/types";
import { getUserIdCookie } from "@/utils/cookies";
import { useAppStore } from "@/stores";
import type { CampaignKey } from "@/campaigns/campaign-key";

export type SendActionOptions = {
  /** Campaign key for the optimistic `joinedById` write, so a failed opt-in can
   *  roll back the exact campaign it touched. */
  key?: CampaignKey;
  onOptInSuccess?: () => void;
  /** The opt-in did not happen (rejected, never sent, or lost across a disconnect) — after the
   *  optimistic join has been rolled back. Lets a caller that shows its own progress (the opt-in
   *  prompt) say so instead of silently flipping back. */
  onOptInFailure?: () => void;
};

type PendingOptIn = {
  key: CampaignKey | null;
  onSuccess: (() => void) | null;
  onFailure: (() => void) | null;
};

export class ActionHandler extends BaseHandler {
  // Opt-in acks carry no campaign id, but a single WS connection delivers them
  // in send order — so pending opt-ins are correlated FIFO. A single slot would
  // drop the first opt-in's success/rollback when a second is fired before its
  // ack returns (rapid multi-campaign opt-in).
  //
  // FIFO correlation only holds if every enqueued send gets exactly one ack, in
  // order. Two things break that, and both are handled: a send that never leaves
  // the client (socket mid-reconnect) must NOT enqueue (see `sendAction`), and an
  // in-flight opt-in whose ack is lost across a disconnect must be dropped (see
  // `reset`, called on disconnect) — otherwise one stale entry would misattribute
  // every later ack for the rest of the session.
  private pendingOptIns: PendingOptIn[] = [];

  async handle(message: WebSocketResponse): Promise<void> {
    if (!this.validateMessage(message)) {
      return;
    }

    if (message.type?.startsWith("action:")) {
      this.handleActionResponse(
        message as OptInTournamentResponse | OptOutTournamentResponse
      );
    }
  }

  canHandle(messageType: string): boolean {
    return (
      messageType === "action:opt_in" ||
      messageType === "action:opt_out" ||
      messageType === RestartTournament
    );
  }

  /**
   * `action:restart_tournament` for a chase (and a mission tournament): addressed by the
   * campaign, since the widget never sees a tournament pk. Fire-and-forget by design — the
   * backend decides eligibility and marks the player's `tournament_progress` pending, so the
   * answer is the next push zeroing the spins, not the ack; there is no optimistic write to roll
   * back and no FIFO slot to keep (unlike opt-in).
   */
  sendRestart(campaignId: number): boolean {
    const playerId = getUserIdCookie();
    if (!playerId) {
      this.logger.error("Missing player ID for restart", { campaignId });
      return false;
    }
    return WebSocketService.getInstance().send(RestartTournament, {
      player: playerId,
      campaign: campaignId,
    });
  }

  sendAction(
    action: OptInTournament | OptOutTournament,
    campaignId: number,
    options?: SendActionOptions
  ): void {
    const playerId = getUserIdCookie();
    if (!playerId) {
      this.logger.error("Missing player ID for action", { action, campaignId });
      if (action === OptInTournament) {
        this.rollback(options?.key);
        options?.onOptInFailure?.();
      }
      return;
    }
    const delivered = WebSocketService.getInstance().send(action, {
      player: playerId,
      campaign: campaignId,
    });
    if (action !== OptInTournament) return;
    if (delivered) {
      // Enqueue ONLY once the send actually left the client, so a no-op send
      // (mid-reconnect) can't leave a phantom entry that misaligns the queue.
      this.pendingOptIns.push({
        key: options?.key ?? null,
        onSuccess: options?.onOptInSuccess ?? null,
        onFailure: options?.onOptInFailure ?? null,
      });
    } else {
      // The action never went out — undo the optimistic join immediately.
      this.rollback(options?.key);
      options?.onOptInFailure?.();
    }
  }

  /** Drop all in-flight opt-ins and undo their optimistic joins. Called on
   *  disconnect: their acks may be lost across the drop, and the reconnect's
   *  `campaign_progress` push re-establishes the true joined state anyway. */
  reset(): void {
    for (const pending of this.pendingOptIns) {
      this.rollback(pending.key);
      pending.onFailure?.();
    }
    this.pendingOptIns = [];
  }

  private rollback(key: CampaignKey | null | undefined): void {
    if (key != null) useAppStore.getState().setJoined(key, false);
  }

  private handleActionResponse(
    message: OptInTournamentResponse | OptOutTournamentResponse
  ): void {
    if (message.type !== "action:opt_in") {
      return;
    }
    const pending = this.pendingOptIns.shift();
    if (!pending) {
      return;
    }
    if (message.result === RESULT.OK) {
      pending.onSuccess?.();
    } else {
      this.rollback(pending.key);
      pending.onFailure?.();
    }
  }
}

export const actionHandler = new ActionHandler();
